var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PacemRectElement_1;
import { CustomElement, Watch, PropertyConverters, P, Utils } from '@pacem/pacem-core';
import { ShapeElement } from './types';
import { TAG_MIDDLE_NAME } from './constants';
//namespace Pacem.Components.Drawing {
/*
 r="4"   // every corner has a rx equalt o ry equal to 8 units
 r="4,8 cut" // every corner has a rx equal to 4px and a ry equal to 8px (cut corners)
 r="4,8 cut 5,6 3,20% round 50%,4" // top-left corner has a rx equal to 4px and a ry equal to 8px (cut corner), top-right corner has a rx equal to 5px and a ry equal to 6px,
                      // bottom-right corner has a rx equal to 3px and a ry equal to 20% the height,
                      // bottom-left corner has a rx equal to 50% the width and a ry equal to 4px.

 */
function parseCornerRadius(radius) {
    const rx = radius[2], ry = radius[4] ?? rx, type = radius[6] === 'cut' ? CornerType.Cut : CornerType.Rounded;
    return { rx: parseCornerRadiusComponent(rx), ry: parseCornerRadiusComponent(ry), type };
}
function parseCornerRadiusComponent(radius) {
    const val = parseFloat(radius);
    return { value: val, unit: radius.endsWith('%') ? 'pct' : 'u' };
}
function stringifyCornerRadiusComponent(radius) {
    return `${radius.value}${radius.unit === 'pct' ? '%' : ''}`;
}
const CORNERS_PATTERN = /(([\d\.]+%?)(\s*,?\s*([\d\.]+%?))?(\s+(cut|round))?)/g;
function parseCornerRadii(radii) {
    let numbers;
    const acc = [];
    while (numbers = CORNERS_PATTERN.exec(radii)) {
        acc.push(numbers);
    }
    switch (acc.length) {
        case 1:
            const single = parseCornerRadius(acc[0]);
            return [single, single, single, single];
        case 4:
            const topLeft = parseCornerRadius(acc[0]);
            const topRight = parseCornerRadius(acc[1]);
            const bottomRight = parseCornerRadius(acc[2]);
            const bottomLeft = parseCornerRadius(acc[3]);
            return [topLeft, topRight, bottomRight, bottomLeft];
        default:
            return null;
    }
}
function stringifyCornerRadii(radii) {
    const topLeft = radii[0], topRight = radii[1], bottomRight = radii[2], bottomLeft = radii[3];
    const tlX = stringifyCornerRadiusComponent(topLeft.rx), tlY = stringifyCornerRadiusComponent(topLeft.ry);
    const trX = stringifyCornerRadiusComponent(topRight.rx), trY = stringifyCornerRadiusComponent(topRight.ry);
    const brX = stringifyCornerRadiusComponent(bottomRight.rx), brY = stringifyCornerRadiusComponent(bottomRight.ry);
    const blX = stringifyCornerRadiusComponent(bottomLeft.rx), blY = stringifyCornerRadiusComponent(bottomLeft.ry);
    return `${tlX},${tlY} ${trX},${trY} ${brX},${brY} ${blX},${blY}`;
}
export var CornerType;
(function (CornerType) {
    CornerType["Rounded"] = "rounded";
    CornerType["Cut"] = "cut";
})(CornerType || (CornerType = {}));
let PacemRectElement = PacemRectElement_1 = class PacemRectElement extends ShapeElement {
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        if (!first && (name === 'x' || name === 'y' || name === 'w' || name === 'h' || name === 'r' || name === 'cornerType')) {
            this.recomputeShape();
        }
    }
    getPathData() {
        const x = this.x, y = this.y, w = this.w, h = this.h;
        let r = this.r ?? { rx: { value: 0 }, ry: { value: 0 }, type: CornerType.Rounded };
        if (!Utils.isArray(r)) {
            r = [r, r, r, r];
        }
        // forgiving behavior
        r = r.map(i => { return typeof i === 'number' ? { rx: { value: i }, ry: { value: i }, type: this.cornerType } : i; });
        if (!Utils.isNull(x) && !Utils.isNull(y) && !Utils.isNull(w) && !Utils.isNull(h)) {
            return PacemRectElement_1.getPathData(x, y, w, h, r);
        }
        return null;
    }
    getShapeGeometry() {
        const x = this.x, y = this.y, x1 = x + this.w, y1 = y + this.h;
        return {
            pathData: this.getPathData(), vertices: [{ x, y }, { x: x1, y }, { x: x1, y: y1 }, { x, y: y1 }], boundingRect: { x, y, width: this.w, height: this.h }
        };
    }
    static getPathData(x = NaN, y = NaN, w = NaN, h = NaN, r = null) {
        if (!r) {
            return `M ${x} ${y} h ${w} v ${h} h ${-w} z`;
        }
        if (!Utils.isArray(r)) {
            r = [r, r, r, r];
        }
        const tl = r[0], tr = r[1], br = r[2], bl = r[3];
        const vx = (rx) => rx.unit === 'pct' ? rx.value * .01 * w : rx.value;
        const vy = (ry) => ry.unit === 'pct' ? ry.value * .01 * h : ry.value;
        const tlX = vx(tl.rx), tlY = vy(tl.ry);
        const trX = vx(tr.rx), trY = vy(tr.ry);
        const brX = vx(br.rx), brY = vy(br.ry);
        const blX = vx(bl.rx), blY = vy(bl.ry);
        let retval = `M ${x},${y + tlY}`;
        // top-left
        switch (tl.type) {
            case 'cut':
                retval += ` l ${tlX},${-tlY}`;
                break;
            default:
                retval += ` a ${tlX} ${tlY} 0 0 1 ${tlX} ${-tlY}`;
                break;
        }
        retval += ` h ${w - tlX - trX}`;
        // top-right
        switch (tr.type) {
            case 'cut':
                retval += ` l ${trX},${trY}`;
                break;
            default:
                retval += ` a ${trX} ${trY} 0 0 1 ${trX} ${trY}`;
                break;
        }
        retval += ` v ${h - trY - brY}`;
        // bottom-right
        switch (br.type) {
            case 'cut':
                retval += ` l ${-brX},${brY}`;
                break;
            default:
                retval += ` a ${brX} ${brY} 0 0 1 ${-brX} ${brY}`;
                break;
        }
        retval += ` h ${-(w - brX - blX)}`;
        // bottom-left
        switch (bl.type) {
            case 'cut':
                retval += ` l ${-blX},${-blY}`;
                break;
            default:
                retval += ` a ${blX} ${blY} 0 0 1 ${-blX} ${-blY}`;
                break;
        }
        return retval + ' z';
    }
};
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRectElement.prototype, "x", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRectElement.prototype, "y", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRectElement.prototype, "w", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Number })
], PacemRectElement.prototype, "h", void 0);
__decorate([
    Watch({
        emit: false, converter: {
            convert: attr => parseCornerRadii(attr),
            convertBack: (radii) => stringifyCornerRadii(radii)
        }
    })
], PacemRectElement.prototype, "r", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemRectElement.prototype, "cornerType", void 0);
PacemRectElement = PacemRectElement_1 = __decorate([
    CustomElement({ tagName: P + '-' + TAG_MIDDLE_NAME + '-rect' })
], PacemRectElement);
export { PacemRectElement };
